import PageHeader from "@/components/PageHeader";
import { Target, Eye, Award, Users, Compass, ShieldCheck, Heart, Scale, Leaf } from "lucide-react";
import mapImage from '../assets/CRDO_Operational Areas_update.png';
import organogramImage from '../assets/CRDO_Organogram.png';

const values = [
  { icon: Heart, t: "Honesty", d: "Integrity  in every action , truth in every word." },
  { icon: Scale, t: "Transparency and Accountability", d: "Clear actions, responsible impact." },
  { icon: ShieldCheck, t: "Mutual respect", d: "We value diversity, fostering unity." },
  { icon: Leaf, t: "Gender sensitivity and Equity", d: "Empowering all, leaving none behind." },
  { icon: Users, t: "Cost efficiency", d: "Maximizing impact, minimizing waste." },
];

const regions = ["Ethiopia (Somali Region)", "Somalia", "Northern Kenya"];

const About = () => (
  <>
    <PageHeader
      eyebrow="About us"
      title="Locally led. Globally connected. Built for resilience."
      subtitle="We are an Ethiopian, Somalia, and Kenyan registered NGO working at the intersection of humanitarian response, development, and climate adaptation."
    />

    {/* Background */}
    <section className="section-padding">
      <div className="container-narrow grid gap-12 md:grid-cols-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Background</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-primary">Our story</h2>
        </div>
        <div className="space-y-4 text-muted-foreground md:col-span-2">
          <p>
            The Climate Resilient and Development Organization (CRDO) is a non-profit, non-governmental organization initially established in 2015. Recognizing the increasing demand for its services, CRDO was officially registered in Ethiopia on November 21, 2024, under Ethiopian Federal Civil Society Organization guidelines. It has affiliate offices in Somalia and Kenya.
          </p>
          <p>
            CRDO works to transform the livelihoods of pastoral and agro-pastoral populations by promoting market-driven, climate-resilient livestock and agriculture systems, while addressing the impacts of recurrent shocks, including drought, conflict, and climate change.
          </p>
          <p>
            Anchored in a community-centered and market systems approach, CRDO designs and implements integrated programs that link humanitarian response with long-term development. The organization prioritizes value chain development, private sector engagement, and climate-smart solutions to enable communities to transition from subsistence to sustainable, income-generating livelihoods, while enhancing social inclusion, gender equity, and environmental sustainability.
          </p>
          <p>
            CRDO operates across Ethiopia (Somali Region), Somalia, and Northern Kenya, focusing on dryland and fragile contexts where resilience building is critical. With a strong field presence and partnerships with government institutions, local organizations, and private sector actors, CRDO delivers context-specific, scalable solutions that improve livelihoods, strengthen local systems, and support peaceful coexistence among communities.
          </p>
        </div>
      </div>
    </section>

    {/* Operational scope */}
    <section className="section-padding bg-secondary">
      <div className="container-wide">
        <div className="mb-12 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Operational Scope and Partnerships</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">Where we work</h2>
          <p className="mt-4 text-muted-foreground">
           CRDO is active in Ethiopia’s Somali Regional State (Dawa Zone), Somalia (Galmudug State, Mogadishu (headquarters),
            Hirshabelle State, Puntland State, Somaliland State, and South West State). CRDO also has prescence in Kenya along the Kenya-Somalia border 
            (Mandera, Garissa, and Wajir in the North Eastern region; Moyale, Marsabit, and Isiolo in the Eastern region;
             and Turkana in the North-western region) in order to reach vulnerable communities and individuals 
             who are In need of our support and intervention. 
             CRDO fosters strategic partnerships with international agencies (including UN bodies), National and Local governments,
              Community-based organizations, and NGOs to enhance emergency response and development programs.
          </p>
        </div>
         <div className="grid gap-8 md:grid-cols-2">
          {/* Stylized map */}
          <div className="aspect-square overflow-hidden rounded-lg border border-border bg-card p-8 shadow-[var(--shadow-card)]">
            <img 
              src={mapImage} 
              alt="Map of CRDO Operational Regions" 
              className="map-image" 
            />
          </div>
          <div>
            <h3 className="font-serif text-xl font-semibold text-primary">Regions of Operation</h3>
            <ul className="mt-5 grid grid-cols-2 gap-3">
              {regions.map((r) => (
                <li key={r} className="flex items-center gap-2.5 rounded-md border border-border bg-card px-4 py-3 text-sm font-medium text-foreground">
                  <span className="h-2 w-2 rounded-full bg-accent" /> {r}
                </li>
              ))}
            </ul>
            <h3 className="mt-10 font-serif text-xl font-semibold text-primary">Key partners</h3>
            <p className="mt-3 text-muted-foreground">
              UNICEF · WHO · UNHCR · OCHA · WFP · FAO · Federal Government of Ethiopia. Federal Government of Somalia · Republic of Kenya. State authorities · Local CBOs
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Mission Vision Goals */}
    <section className="section-padding bg-secondary">
     <div className="container-wide">
      <div className="mb-14 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Mission, Vision, and Goals</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">We are destined to serve...</h2>
        </div>
      </div>
      <div className="container-wide grid gap-6 md:grid-cols-3">
        {[
          { icon: Target, t: "Mission", d: "Mission: Strengthening vulnerable communities through climate-smart and inclusive livelihood solutions." },
          { icon: Eye, t: "Vision", d: "Vision: Resilient and economically empowered communities across the Horn of Africa." },
          { icon: Award, t: "Goal", d: "Goal:  Enhancing resilience through integrated livestock, agriculture, and emergency support systems." },
        ].map(({ icon: Icon, t, d }) => (
          <article key={t} className="rounded-lg border border-border bg-card p-8 shadow-[var(--shadow-card)]">
            <span className="grid h-12 w-12 place-items-center rounded-md bg-accent text-accent-foreground">
              <Icon className="h-6 w-6" />
            </span>
            <h3 className="mt-5 font-serif text-2xl font-semibold text-primary">{t}</h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">{d}</p>
          </article>
        ))}
      </div>
    </section>

    {/* Values */}
    <section className="section-padding bg-secondary">
      <div className="container-wide">
        <div className="mb-12 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Values & principles</p>
          <h2 className="mt-2 font-serif text-1xl font-semibold text-primary md:text-4xl">CRDO encourages the following Values to be practiced both at individual and Organizational level.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {values.map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-serif text-lg font-semibold text-primary">{t}</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Org structure */}
    <section className="section-padding">
      <div className="container-narrow">
        <div className="mb-12">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">Organizational structure</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-primary md:text-4xl">CRDO’s governance framework ensures efficiency, flexibility, and accountability. Key management organs include:</h2>
        </div>
        <div className="space-y-3">
          {[
            { l: "Chairman & Board of Directors", d: "Strategic governance and oversight" },
            { l: "Executive Leadership", d: "Organizational leadership" },
            { l: "Regional Offices", d: "Ethiopia, Somalia, Kenya" },
            { l: "Country Operations", d: "Country Project Coordinators" },
            { l: "Field Offices", d: "Frontline delivery and accountability" },
          ].map((row, i) => (
            <div key={row.l} className="flex items-start gap-5 rounded-lg border border-border bg-card p-5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground font-serif text-sm font-semibold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="font-serif text-lg font-semibold text-primary">{row.l}</p>
                <p className="text-sm text-muted-foreground">{row.d}</p>
              </div>
            </div>
            
          ))}
        </div>
        <img 
          src={organogramImage} 
          alt="CRDO Organogram" 
          className="organogram-image" 
        />
      </div>
    </section>
  </>
);

export default About;
